import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import { 
  Event as EventIcon, 
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Description as DescriptionIcon,
  OpenInNew as OpenInNewIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon
} from "@mui/icons-material";
import MKButton from "./MKButton";
import { SUCCESS_GREEN, TRANSPARENT_PINK_05, PRIMARY_PINK, PRIMARY_PINK_DARK } from "../constants";

function EventDetailsModal({ open, onClose, event, clubName }) {
  const navigate = useNavigate();
  
  if (!event) return null;

  const formatDateTime = (dateString) => {
    if (!dateString) return "TBD";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return "";
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes > 0 ? `${diffMinutes}m` : ''}`;
    } else {
      return `${diffMinutes}m`;
    }
  };

  const isUpcoming = (endTime) => {
    if (!endTime) return false;
    return new Date(endTime) > new Date();
  };

  const handleViewFullEvent = () => {
    onClose(); // Close the modal first
    navigate(`/events/${event.id}`); // Navigate to the event landing page
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <EventIcon sx={{ color: PRIMARY_PINK }} />
          <Typography variant="h5" component="div" fontWeight="bold">
            {event.title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Hosted by {clubName}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          {isUpcoming(event.end_time) && (
            <Chip 
              label="Upcoming" 
              sx={{ 
                color: PRIMARY_PINK, 
                borderColor: PRIMARY_PINK,
                '& .MuiChip-label': { color: PRIMARY_PINK }
              }}
              variant="outlined"
              size="small" 
            />
          )}
                     {event.rsvp_enabled && (
             <Chip 
               icon={<GroupIcon />}
               label="RSVP Available" 
               sx={{ 
                 color: SUCCESS_GREEN, 
                 borderColor: SUCCESS_GREEN,
                 '& .MuiChip-label': { color: SUCCESS_GREEN }
               }}
               variant="outlined"
               size="small" 
             />
           )}
           {event.ticket_available && (
             <Chip 
               icon={<CheckCircleIcon />}
               label="Tickets Available" 
               sx={{ 
                 color: PRIMARY_PINK, 
                 borderColor: PRIMARY_PINK,
                 '& .MuiChip-label': { color: PRIMARY_PINK }
               }}
               variant="outlined"
               size="small" 
             />
           )}
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {/* Event Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Date and Time */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <TimeIcon sx={{ color: PRIMARY_PINK, mt: 0.5 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Date & Time
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {formatDateTime(event.start_time)}
              </Typography>
              {event.end_time && event.end_time !== event.start_time && (
                <Typography variant="body2" color="text.secondary">
                  Duration: {formatDuration(event.start_time, event.end_time)}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Flyer Image - moved here from top */}
          {event.flyer_url && (
            <Box sx={{ ml: 4, mb: 1 }}>
              <Box
                component="img"
                src={event.flyer_url}
                alt={`Flyer for ${event.title}`}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 300,
                  borderRadius: 2,
                  boxShadow: 2,
                  objectFit: 'contain',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: 4,
                  }
                }}
                onClick={() => window.open(event.flyer_url, '_blank')}
                title="Click to view full size"
              />
            </Box>
          )}

          <Divider />

          {/* Location */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <LocationIcon sx={{ color: PRIMARY_PINK, mt: 0.5 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Location
              </Typography>
              <Typography variant="body1">
                {event.private_event ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Location hidden for private event
                  </Typography>
                ) : event.hide_location_until_rsvp ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Location hidden until RSVP'd
                  </Typography>
                ) : event.location_url ? (
                  <Typography
                    component="a"
                    href={event.location_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: PRIMARY_PINK,
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {event.location_display_name || event.location || 'TBD'}
                  </Typography>
                ) : (
                  event.location || "Location TBD"
                )}
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Description */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <DescriptionIcon sx={{ color: PRIMARY_PINK, mt: 0.5 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                About This Event
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  lineHeight: 1.6,
                  whiteSpace: 'pre-line'
                }}
              >
                {event.description || "No description available."}
              </Typography>
            </Box>
          </Box>

          {/* RSVP Information */}
          {event.rsvp_enabled && (
            <>
              <Divider />
                             <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                 <GroupIcon sx={{ color: SUCCESS_GREEN, mt: 0.5 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    RSVP Information
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    This is a <strong>FREE</strong> event with RSVP available
                  </Typography>
                  {event.rsvp_limit && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>RSVP Limit:</strong> {event.rsvp_count || 0} / {event.rsvp_limit} spots taken
                    </Typography>
                  )}
                  {event.rsvp_deadline && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>RSVP Deadline:</strong> {formatDateTime(event.rsvp_deadline)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}

          {/* Ticket Information */}
          {event.ticket_available && (
            <>
              <Divider />
                             <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                 <CheckCircleIcon sx={{ color: PRIMARY_PINK, mt: 0.5 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Ticket Information
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Price:</strong> ${parseFloat(event.ticket_price || 0).toFixed(2)} {event.ticket_currency}
                  </Typography>
                  {event.ticket_description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {event.ticket_description}
                    </Typography>
                  )}
                  {event.ticket_quantity && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Available:</strong> {event.ticket_quantity} tickets
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* Additional Info */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Created:</strong> {event.created_at ? new Date(event.created_at).toLocaleDateString() : "Unknown"}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <MKButton 
          onClick={onClose} 
          variant="outlined"
          sx={{
            color: "#000000",
            borderColor: "#000000",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              borderColor: "#000000",
              backgroundColor: "rgba(0,0,0,0.08)"
            }
          }}
        >
          Close
        </MKButton>
        <MKButton 
          onClick={handleViewFullEvent} 
          variant="contained" 
          startIcon={<OpenInNewIcon />}
          sx={{
            fontWeight: 600,
            textTransform: "none",
            bgcolor: PRIMARY_PINK,
            color: '#ffffff',
            "&:hover": {
              bgcolor: PRIMARY_PINK_DARK
            }
          }}
        >
          View Full Event
        </MKButton>
      </DialogActions>
    </Dialog>
  );
}

export default EventDetailsModal; 